import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InquiryDayItem, InquiryHotelItem, InquiryItem } from "@/types/database";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { useUserRole } from "@/lib/useUserRole";

interface InquiryTotalSummaryProps {
  days: InquiryDayItem[];
  hotels: Omit<InquiryHotelItem, 'id' | 'inquiry_id' | 'created_at' | 'updated_at'>[];
  adult_count: number;
  child_count: number;
  totalUSD?: number;
  onChangeTotalUSD?: (value: number) => void;
}

export function InquiryTotalSummary({ 
  days, 
  hotels, 
  adult_count, 
  child_count, 
  totalUSD: initialTotalUSD,
  onChangeTotalUSD
}: InquiryTotalSummaryProps) {
  // Get user role to determine if price editing is allowed
  const { role } = useUserRole();
  const canEditPrices = role === 'admin' || role === 'agent';
  // Format currency functions
  const formatIDR = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Convert IDR to USD - no automatic conversion
  const convertToUSD = (idrValue: number) => {
    // Return the input value without conversion
    return idrValue;
  };

  // State for the USD total - editable but not auto-generated
  const [totalUSDValue, setTotalUSDValue] = useState<number>(initialTotalUSD || 0);

  // Handle USD input change
  const handleUSDChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditPrices) return;
    
    const value = parseFloat(e.target.value) || 0;
    setTotalUSDValue(value);
    if (onChangeTotalUSD) {
      onChangeTotalUSD(value);
    }
  };

  // Calculate totals
  const totalTransport = days.reduce((sum, day) => sum + (day.transport_cost || 0), 0);
  
  // Calculate parking from captured_tours
  const totalParking = days.reduce((sum, day) => {
    if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`]) {
      console.log(`Day ${day.day_number} missing captured_tours for parking calculation`);
      return sum;
    }
    const dayTours = day.captured_tours[`day_${day.day_number}`];
    if (!dayTours.parking) {
      console.log(`Day ${day.day_number} missing parking data in captured_tours`);
      return sum;
    }
    const parkingTotal = Object.values(dayTours.parking).reduce((s, cost) => s + (cost || 0), 0);
    console.log(`Day ${day.day_number} parking total: ${parkingTotal}`);
    return sum + parkingTotal;
  }, 0);

  // Calculate entrance fees from captured_tours
  const { totalAdultEntrance, totalChildEntrance } = days.reduce((acc, day) => {
    if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`]) {
      console.log(`Day ${day.day_number} missing captured_tours for entrance fee calculation`);
      return acc;
    }
    const dayTours = day.captured_tours[`day_${day.day_number}`];
    if (!dayTours.tours || !dayTours.entrance_adult || !dayTours.entrance_child) {
      console.log(`Day ${day.day_number} missing tours or entrance fee data in captured_tours`);
      return acc;
    }
    
    dayTours.tours.forEach(tour => {
      const adultFee = (dayTours.entrance_adult[tour] || 0) * adult_count;
      const childFee = (dayTours.entrance_child[tour] || 0) * child_count;
      acc.totalAdultEntrance += adultFee;
      acc.totalChildEntrance += childFee;
    });
    
    return acc;
  }, { totalAdultEntrance: 0, totalChildEntrance: 0 });

  const totalEntrance = totalAdultEntrance + totalChildEntrance;

  const totalMealCost = days.reduce((sum, day) => sum + (day.meal_cost || 0), 0);
  const totalOtherCost = days.reduce((sum, day) => sum + (day.other_cost || 0), 0);
  const totalHotelPrice = hotels.reduce((sum, hotel) => sum + (hotel.total_price || 0), 0);

  const grandTotal = totalTransport + totalParking + totalEntrance + totalMealCost + totalOtherCost + totalHotelPrice;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Grand Total Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 font-mono">
          <div className="flex justify-between">
            <span>Total Transport Costing:</span>
            <span>{formatIDR(totalTransport)}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Parking Costing:</span>
            <span>{formatIDR(totalParking)}</span>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>Adult Entrance Fee ({adult_count} x):</span>
              <span>{formatIDR(totalAdultEntrance)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Child Entrance Fee ({child_count} x):</span>
              <span>{formatIDR(totalChildEntrance)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Total Entrance Fee:</span>
              <span>{formatIDR(totalEntrance)}</span>
            </div>
          </div>
          <div className="flex justify-between">
            <span>Total Meal Cost:</span>
            <span>{formatIDR(totalMealCost)}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Other Cost:</span>
            <span>{formatIDR(totalOtherCost)}</span>
          </div>
          <div className="flex justify-between">
            <span>Total Hotel Price:</span>
            <span>{formatIDR(totalHotelPrice)}</span>
          </div>
          <div className="h-px bg-border my-2" />
          <div className="flex justify-between font-bold">
            <span>Grand Total:</span>
            <span>{formatIDR(grandTotal)}</span>
          </div>
          <div className="flex items-center justify-between font-bold text-primary">
            <span>Total USD:</span>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={totalUSDValue}
                onChange={handleUSDChange}
                className="w-32 text-right h-8 text-xs"
                step="0.01"
                disabled={!canEditPrices}
                readOnly={!canEditPrices}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
